import { can } from "../../../../../shared/auth/access";
import type { SessionUser } from "../../../../../shared/auth/access";
import type { ProcurementFlowStep } from "../../../../../shared/components/ProcurementFlow";
import type { RequestDetailData, RequestOfferEvaluation, Attachment, Offer } from "../../../domain/request-detail-types";
import { parseOfferAmountValue } from "../../../domain/offer-entry";

export function requestOfferComparison(detail: RequestDetailData) {
    if (!detail?.offers.length) return null;
    const candidates = detail.offers;
    return {
      lowest: [...candidates].sort(
        (left, right) =>
          parseOfferAmountValue(left.amount) -
          parseOfferAmountValue(right.amount),
      )[0],
      fastest: [...candidates].sort(
        (left, right) => left.delivery_days - right.delivery_days,
      )[0],
      longestPayment: [...candidates].sort(
        (left, right) => right.payment_days - left.payment_days,
      )[0],
    };
  }

export function requestDetailViewModel(detail: RequestDetailData, evaluation: RequestOfferEvaluation | null, user: SessionUser) {
  const comparison = requestOfferComparison(detail);
const getAttachmentOffer = (attachment: Attachment) =>
    attachment.offer_no
      ? (detail?.offers.find(
          (offer) => offer.offer_no === attachment.offer_no,
        ) ?? null)
      : detail?.offers.length === 1
        ? detail.offers[0]
        : null;
const getOfferAttachments = (offer: Offer) =>
    (detail?.attachments ?? []).filter(
      (attachment) =>
        attachment.offer_no === offer.offer_no ||
        (!attachment.offer_no && detail?.offers.length === 1),
    );
const latestRevision = [...detail.approvals]
    .reverse()
    .find((approval) => approval.status === "REVISION_REQUESTED");
const activeCorrection =
    ["Revizyon", "Düzeltme istendi"].includes(detail.status) &&
    latestRevision?.decision_note
      ? latestRevision
      : null;
const isCompleted = detail.status === "Onaylandı";
const managerDecision = detail.approvals.find(
    (approval) => approval.role === "SATIN_ALMA_MUDURU",
  );
const generalManagerDecision = detail.approvals.find(
    (approval) => approval.role === "GENEL_MUDUR",
  );
const finalSelectionChanged =
    generalManagerDecision?.decision_kind === "GM_FINAL_OVERRIDE";
const completedDecisionDataMissing =
    isCompleted &&
    (!detail.recommendation ||
      !managerDecision?.decided_offer_id ||
      !generalManagerDecision?.decided_offer_id);
const canEditPlanningRevision =
    ["Revizyon", "Düzeltme istendi"].includes(detail.status) &&
    (!latestRevision?.revision_target_role ||
      latestRevision.revision_target_role === "PLANLAMA");
const detailFlowSteps: ProcurementFlowStep[] = [
    { role: "SATIN_ALMA_MUDURU", label: "Satın Alma Müdürü" },
    { role: "GENEL_MUDUR", label: "Genel Müdür" },
  ].map((step) => {
    const approval = detail.approvals.find((item) => item.role === step.role);
    const status: ProcurementFlowStep["status"] = approval
      ? approval.status === "APPROVED"
        ? "Onaylandı"
        : approval.status === "REJECTED"
          ? "Reddedildi"
          : approval.status === "PENDING"
            ? "Bekliyor"
            : "Sırada"
      : "Sırada";
    return {
      ...step,
      status,
      actor: approval?.decided_by ?? undefined,
      decidedAt: approval?.decided_at ?? undefined,
    };
  });
const specialistFlowPresentation =
    user.role === "SATIN_ALMA" &&
    ["Satın Almaya iletildi", "Teklif toplanıyor"].includes(detail.status)
      ? {
          eyebrow: "Sıradaki işlem",
          title:
            detail.status === "Satın Almaya iletildi"
              ? "Teklif çalışmasını başlatın"
              : "Teklifleri tamamlayın",
          description:
            detail.status === "Satın Almaya iletildi"
              ? "Kategoriye uygun tedarikçilerden teklif toplanması bekleniyor."
              : "Karşılaştırma için geçerli teklifleri ve ticari koşulları tamamlayın.",
          ownerLabel: "Sonraki aşama",
          owner: "Satın Alma Müdürü onayı",
          ownerNote: "Uzman kararı tamamlandıktan sonra",
        }
      : undefined;
const canRouteRequest =
    !isCompleted &&
    user.role === "SATIN_ALMA" &&
    can(user, "request.manage") &&
    detail.status === "Onaya gönderilmeye hazır";
const canEditRequest =
    !isCompleted &&
    ((user.role === "SATIN_ALMA" &&
      can(user, "request.procurement.create") &&
      detail.canEditRequestDetails === true) ||
      (can(user, "request.create") &&
        (["Satın Almaya iletildi", "Taslak"].includes(detail.status) ||
          canEditPlanningRevision)));
const eligibleDecisionOffers = [...(evaluation?.offers ?? [])]
    .filter((offer) => !offer.exclusionReasons.length)
    .sort(
      (left, right) =>
        (left.rank ?? Number.MAX_SAFE_INTEGER) -
          (right.rank ?? Number.MAX_SAFE_INTEGER) ||
        (right.totalScore ?? Number.NEGATIVE_INFINITY) -
          (left.totalScore ?? Number.NEGATIVE_INFINITY) ||
        left.offerNo.localeCompare(right.offerNo, "tr"),
    );
const canManageSpecialistDecision = [
    "Satın Almaya iletildi",
    "Teklif toplanıyor",
    "Onaya gönderilmeye hazır",
    "Düzeltme istendi",
  ].includes(detail.status) && detail.activeCorrectionType !== "REQUEST_DETAILS";
const hasOfferChangeLock = detail.approvals.some((approval) =>
    ["QUEUED", "PENDING", "APPROVED"].includes(approval.status),
  ) || Boolean(detail.recommendation);
const canChangeOffers = detail.status === "Düzeltme istendi"
    ? latestRevision?.revision_target_role === "SATIN_ALMA"
    : !hasOfferChangeLock;
const canManageOffers =
    can(user, "offer.manage") && canManageSpecialistDecision && canChangeOffers;
const canMakeSpecialistDecision =
    user.role === "SATIN_ALMA" &&
    !isCompleted &&
    canManageSpecialistDecision &&
    Boolean(evaluation) &&
    ["COMPLETED", "SINGLE_OFFER"].includes(evaluation?.status ?? "") &&
    eligibleDecisionOffers.length > 0;
const evaluationBlocker = !detail.offers.length
    ? "Karşılaştırmayı başlatmak için ilk tedarikçi teklifini kaydedin."
    : !evaluation
      ? "Teklifler var ancak karar değerlendirmesi henüz oluşturulmamış."
      : evaluation.status === "EXCHANGE_RATE_MISSING"
        ? "Farklı para birimlerindeki teklifleri karşılaştırmak için güncel kur bilgisi gerekiyor."
        : evaluation.status === "NO_ELIGIBLE_OFFER" ||
            eligibleDecisionOffers.length === 0
          ? "Mevcut teklifler karar koşullarını karşılamıyor. Teklif bilgilerini kontrol edip değerlendirmeyi yenileyin."
          : "Tekliflerdeki son değişikliklerin karara yansıması için değerlendirmeyi yenileyin.";
const decisionPanelContent =
    detail.status === "Onay bekliyor"
      ? {
          title: "Onay süreci",
          description:
            "Uzman kararı kaydedildi. Dosya onay sürecinde olduğu için teklifler salt okunur.",
        }
      : isCompleted
        ? {
            title: "Karar sonucu",
            description:
              "Satın alma kararı tamamlandı. Teklifler ve karar kaydı denetim amacıyla salt okunur.",
          }
        : detail.activeCorrectionType === "REQUEST_DETAILS"
          ? {
              title: "Talep düzeltmesi bekleniyor",
              description:
                "Talep sahibi ihtiyaç bilgilerini güncelledikten sonra teklifleri kontrol edip karşılaştırmayı yeniden hazırlayın.",
            }
        : {
            title: "Karar hazırlığı",
            description: canMakeSpecialistDecision
              ? `${detail.offers.length} teklif değerlendirildi. Tedarikçi seçiminizi ve gerekçenizi kaydedebilirsiniz.`
              : evaluationBlocker,
          };
return { comparison, getAttachmentOffer, getOfferAttachments, latestRevision, activeCorrection, isCompleted, managerDecision, generalManagerDecision, finalSelectionChanged, completedDecisionDataMissing, canEditPlanningRevision, detailFlowSteps, specialistFlowPresentation, canRouteRequest, canEditRequest, eligibleDecisionOffers, canManageSpecialistDecision, canManageOffers, canMakeSpecialistDecision, evaluationBlocker, decisionPanelContent };
}
